import React from 'react';
import { Card3D } from '@/components/ui/visual/Card3D';
import { FloatingCard } from '@/components/ui/visual/FloatingCard';
import WaveDecor from '@/components/ui/visual/WaveDecor';
import { CircleDecor } from '@/components/ui/visual/CircleDecor';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { fadeInVariants, listItemVariants, scaleVariants } from '@/lib/animations';
import { ThemeToggle } from '@/components/theme/ThemeToggle';

/**
 * Componente de exemplos do sistema de design
 * Este componente demonstra os diferentes elementos do sistema
 */
const DesignSystemExamples = () => {
  // Exemplo de estado para botão de loading
  const [isLoading, setIsLoading] = React.useState(false);
  
  const handleLoadingClick = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };
  
  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-8">Exemplos do Sistema de Design</h1>
      
      {/* Seção de Temas */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">Temas</h2>
        <div className="flex items-center gap-4 mb-6">
          <span>Alternar tema:</span>
          <ThemeToggle />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-primary text-primary-foreground p-4 rounded-lg">
            Cor primária
          </div>
          <div className="bg-secondary text-secondary-foreground p-4 rounded-lg">
            Cor secundária
          </div>
          <div className="bg-muted text-muted-foreground p-4 rounded-lg">
            Cor muted
          </div>
          <div className="bg-accent text-accent-foreground p-4 rounded-lg">
            Cor accent
          </div>
        </div>
      </section>
      
      {/* Seção de Animações */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">Animações</h2>
        
        <h3 className="text-xl mb-3">Framer Motion</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div 
            variants={fadeInVariants}
            initial="initial"
            animate="animate"
            className="bg-primary/10 p-4 rounded-lg"
          >
            Fade In
          </motion.div>
          
          <motion.div 
            variants={scaleVariants}
            initial="initial"
            animate="animate"
            className="bg-primary/10 p-4 rounded-lg"
          >
            Scale
          </motion.div>
          
          <div className="space-y-2">
            {[1, 2, 3].map((item) => (
              <motion.div
                key={item}
                variants={listItemVariants}
                initial="initial"
                animate="animate"
                custom={item}
                className="bg-primary/10 p-2 rounded-lg"
              >
                Item {item}
              </motion.div>
            ))}
          </div>
        </div>
        
        <h3 className="text-xl mb-3">Animações CSS</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Audio Wave */}
          <div className="p-4 rounded-lg border flex items-center justify-center">
            <div className="audio-wave">
              <div className="audio-wave-bar"></div>
              <div className="audio-wave-bar"></div>
              <div className="audio-wave-bar"></div>
              <div className="audio-wave-bar"></div>
              <div className="audio-wave-bar"></div>
            </div>
          </div>
          
          {/* Floating Card */}
          <div className="card-float p-4 rounded-lg border">
            Card Flutuante
          </div>
          
          {/* Button Loading */}
          <div className="p-4 rounded-lg border flex items-center justify-center">
            <button 
              onClick={handleLoadingClick} 
              className={`px-4 py-2 bg-primary text-primary-foreground rounded-md ${isLoading ? 'btn-loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? 'Carregando...' : 'Clique para carregar'}
            </button>
          </div>
        </div>
      </section>
      
      {/* Seção de Utilitários */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">Utilitários</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="touch-target bg-primary/10 flex items-center justify-center rounded-lg">
            Touch Target
          </div>
          
          <div className="mobile-container bg-primary/10 rounded-lg">
            <div className="mobile-padding">
              <p>Container com padding responsivo</p>
            </div>
          </div>
          
          <div className="bg-semi-transparent p-4 text-white rounded-lg">
            Background semi-transparente
          </div>
          
          <div className="bg-semi-transparent-dark p-4 text-white rounded-lg">
            Background semi-transparente escuro
          </div>
        </div>
        
        <div className="flex flex-wrap gap-4">
          <button className="btn-hover-effect px-4 py-2 bg-primary text-white rounded-md">
            Botão com Hover Effect
          </button>
        </div>
      </section>
      
      {/* Seção de Componentes Visuais */}
      <section className="mb-12 relative">
        <h2 className="text-2xl font-semibold mb-4">Componentes Visuais</h2>
        
        <div className="relative overflow-hidden">
          <WaveDecor className="top-0 opacity-20" />
          <CircleDecor className="absolute top-10 right-10" animated size="md" />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 mt-16">
            <FloatingCard>
              <h3 className="text-lg font-medium mb-2">Floating Card</h3>
              <p>Este cartão flutua suavemente na página.</p>
            </FloatingCard>
            
            <Card3D className="p-6">
              <h3 className="text-lg font-medium mb-2">Card 3D</h3>
              <p>Este cartão tem efeito 3D ao passar o mouse (apenas desktop).</p>
            </Card3D>
          </div>
        </div>
      </section>
      
      {/* Seção de Botões */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">Botões</h2>
        
        <div className="flex flex-wrap gap-4">
          <Button>Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="link">Link</Button>
        </div>
        
        <div className="flex flex-wrap gap-4 mt-4">
          <Button size="sm">Small</Button>
          <Button>Default</Button>
          <Button size="lg">Large</Button>
          <Button size="icon"><span>+</span></Button>
        </div>
        
        <div className="flex flex-wrap gap-4 mt-4">
          <Button isLoading loadingText="Carregando...">Loading</Button>
        </div>
      </section>
    </div>
  );
};

export default DesignSystemExamples;
